// Acceptance of the terms (F8). After sign-in, when the server says the person has not accepted the current Terms of
// Service (GET /me/legal: needs_acceptance), a calm blocking dialog comes before the app. Checkout and credit packs
// answer 409 `terms_required` until then: `useTerms().withTerms(run)` opens the same dialog and runs the call again
// once the terms are accepted. A server without the route, a failure to read it, or a session on an API key asks
// nothing: the server holds the rule at sign-up and at checkout either way.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api.ts";
import { BRAND } from "../../brand.ts";
import { mocksOn } from "../../data/flags.ts";
import {
  createLegalSource,
  getLegal,
  isTermsOutdated,
  keyCannotAccept,
  type LegalSource,
  type LegalStatus,
  legalSource,
  retryAfterTerms,
} from "../../data/legal.ts";
import { errText } from "../app.ts";
import { Button, Dialog } from "../kit.tsx";
import { Splash } from "../MarkLoader.tsx";
import "./legal.css";

interface LegalContextValue {
  /** Runs a call that needs accepted terms: when it answers `terms_required`, asks first and then runs it again. */
  withTerms<T>(run: () => Promise<T>): Promise<T>;
}

const LegalContext = createContext<LegalContextValue>({ withTerms: (run) => run() });
export const useTerms = (): LegalContextValue => useContext(LegalContext);

/** The terms dialog. `blocking`: nothing else can be done but accept or sign out (the gate); else it can be closed. */
export function TermsDialog({
  source,
  status,
  blocking,
  onAccepted,
  onSignOut,
  onCancel,
}: {
  source: LegalSource;
  status: LegalStatus | null;
  blocking: boolean;
  onAccepted: (s: LegalStatus) => void;
  onSignOut?: () => void;
  onCancel?: () => void;
}) {
  const docs = getLegal();
  const [version, setVersion] = useState(status?.termsVersion ?? docs.terms.version);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      onAccepted(await source.accept(version));
    } catch (e) {
      setBusy(false);
      if (isTermsOutdated(e)) {
        // the terms moved on while the dialog was open: read them again
        const fresh = await source.status().catch(() => null);
        if (fresh && !fresh.needsAcceptance) return onAccepted(fresh);
        if (fresh) setVersion(fresh.termsVersion);
        setAgreed(false);
        return setError("Our terms were updated just now. Read them again, then accept.");
      }
      setError(errText(e));
    }
  }

  const link = (href: string, label: string) => (
    <a className="link" href={href} target="_blank" rel="noopener noreferrer">
      {label}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );

  return (
    <Dialog
      title="Our terms"
      onClose={() => onCancel?.()}
      closable={!blocking}
      footer={
        <>
          {blocking ? (
            <Button variant="text" onClick={onSignOut}>
              Sign out
            </Button>
          ) : (
            <Button variant="text" onClick={onCancel}>
              Not now
            </Button>
          )}
          <span className="grow" />
          <Button variant="primary" disabled={!agreed || busy} onClick={() => void accept()}>
            {busy ? "Accepting…" : "Accept and continue"}
          </Button>
        </>
      }
    >
      <p className="lg-text">
        To use {BRAND.name}, please accept our Terms of Service. They include the Data Processing Agreement
        for the content you upload. Our Privacy Policy explains what we do with your data.
      </p>
      <ul className="lg-links">
        <li>{link(docs.terms.url, "Terms of Service")}</li>
        <li>{link(docs.dpa.url, "Data Processing Agreement")}</li>
        <li>{link(docs.privacy.url, "Privacy Policy")}</li>
      </ul>
      <label className="lg-agree">
        <input
          type="checkbox"
          data-autofocus
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <span>I agree to the Terms of Service</span>
      </label>
      {error && (
        <p className="err" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

interface Loaded {
  source: LegalSource;
  status: LegalStatus | null;
}

/**
 * Wraps the signed-in app. Reads the acceptance first (a splash meanwhile), shows the blocking dialog when it is
 * needed, and offers `useTerms()` to everything inside. A session on an API key goes straight in: the server refuses
 * the acceptance from a key, so the dialog could only fail, and that build may have no other way to sign in.
 */
export function LegalGate({
  api,
  apiKeySession,
  onSignOut,
  children,
}: {
  api: Api;
  /** signed in with an API key, which the server refuses for accepting the terms */
  apiKeySession: boolean;
  onSignOut: () => void;
  children: ReactNode;
}) {
  // the mock answers for itself: `?mocklegal=pending` shows the dialog to a key's session in `vite dev` too
  const keySession = apiKeySession && !mocksOn();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [asking, setAsking] = useState<{ done: (ok: boolean) => void } | null>(null);
  const pending = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    if (keySession) return;
    let live = true;
    void (async () => {
      let source: LegalSource;
      try {
        source = await legalSource(api);
      } catch {
        source = createLegalSource(api);
      }
      // a failure to read it asks nothing: the server still refuses what needs the terms
      const status = await source.status().catch(() => null);
      if (live) setLoaded({ source, status });
    })();
    return () => {
      live = false;
    };
  }, [api, keySession]);

  const ask = useCallback(() => {
    pending.current ??= new Promise<boolean>((resolve) => {
      setAsking({
        done: (ok) => {
          pending.current = null;
          setAsking(null);
          resolve(ok);
        },
      });
    });
    return pending.current;
  }, []);
  const value = useMemo<LegalContextValue>(
    () => ({ withTerms: (run) => retryAfterTerms(run, keySession ? keyCannotAccept : ask) }),
    [ask, keySession],
  );

  if (keySession) return <LegalContext.Provider value={value}>{children}</LegalContext.Provider>;
  if (!loaded) return <Splash />;
  const accepted = (status: LegalStatus) => {
    setLoaded((l) => l && { ...l, status });
    asking?.done(true);
  };
  if (loaded.status?.needsAcceptance)
    return (
      <div className="splash">
        <TermsDialog
          source={loaded.source}
          status={loaded.status}
          blocking
          onAccepted={accepted}
          onSignOut={onSignOut}
        />
      </div>
    );
  return (
    <LegalContext.Provider value={value}>
      {children}
      {asking && (
        <TermsDialog
          source={loaded.source}
          status={loaded.status}
          blocking={false}
          onAccepted={accepted}
          onCancel={() => asking.done(false)}
        />
      )}
    </LegalContext.Provider>
  );
}
